import { ApiQueryName } from "@/models/api"
import { Department } from "@/models/department"
import type { SpraypaintQueryOptions } from "@/services/types"
import { toHttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"

import { useRenameQueryResult } from "../utils"
import { createQueryKey } from "../utils/createQueryKey"

const getDepartments = async ({
  extraFields,
  includes,
  page,
  pageSize,
  sort,
  where,
}: SpraypaintQueryOptions = {}): Promise<Department[]> => {
  try {
    const response = await Department.where(where ?? {})
      .includes(includes ?? [])
      .order(sort ?? { name: "asc" })
      .page(page ?? 1)
      .per(pageSize ?? 250)
      .selectExtra(extraFields ?? [])
      .all()

    return response.data as Department[]
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseDepartmentsSpraypaintOptions {
  apiQueryOptions?: SpraypaintQueryOptions
  queryOptions?: Omit<UseQueryOptions<Department[]>, "queryKey" | "queryFn">
}

const defaultUseDepartmentsSpraypaintOptions: UseDepartmentsSpraypaintOptions =
  {
    apiQueryOptions: {},
    queryOptions: {},
  }

/**
 * Returns departments
 *
 * @example
 * const {
 *   departmentsSpraypaintData,
 *   departmentsSpraypaintError,
 *   departmentsSpraypaintIsError
 *   departmentsSpraypaintIsPending,
 * } = useDepartmentsSpraypaint({
 *   apiQueryOptions: { where: { organization_id: "1" } },
 *   queryOptions // react query options
 * })
 */
export const useDepartmentsSpraypaint = ({
  apiQueryOptions,
  queryOptions,
}: UseDepartmentsSpraypaintOptions = defaultUseDepartmentsSpraypaintOptions) => {
  // Ensure consumer can omit optional properties
  const options = {
    ...defaultUseDepartmentsSpraypaintOptions,
    apiQueryOptions,
    queryOptions,
  }

  const queryKey = createQueryKey(
    ApiQueryName.DepartmentsSpraypaint,
    "getMany",
    null,
    apiQueryOptions
  )

  const queryFn = async (): Promise<Department[]> => {
    return getDepartments(apiQueryOptions)
  }

  const queryResult = useQuery<Department[]>({
    ...options.queryOptions,
    queryKey,
    queryFn,
    throwOnError: resolveQueryThrowOnError(options.queryOptions?.throwOnError),
  })

  return useRenameQueryResult(queryResult, ApiQueryName.DepartmentsSpraypaint)
}
